import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface MarketingReportRequest {
  email: string;
}

export interface LeadMagnetRequest {
  email: string;
  linkedin_url: string;
  lead_magnet_id?: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_medium?: string;
}

export interface LeadMagnetResponse {
  message: string;
  user: {
    id: string;
    name: string;
    email: string;
    profile_photo: string | null;
    last_active_at: string | null;
    has_completed_setup: number;
    has_connected_to_hubspot: number;
    has_connected_to_google: number;
    company: string | null;
    vat_id: string | null;
    company_invoice: number;
    on_trial: boolean;
    subscribed: boolean;
    credits: number;
    ai_tokens: number;
    plan: string | null;
    serpapi_location: string;
    serpapi_domain: string;
    serpapi_language: string;
    linkedin_url: string;
    public_profile_url: string | null;
    test_code: string | null;
  };
  search: {
    id: number;
    linkedin_profile: string;
    status: number;
    is_loading: boolean;
  };
}

export interface MarketingReportResponse {
  data: {
    report_metadata: {
      creation_date: string;
      client_name: string;
      client_website: string;
    };
    client_descriptors: {
      short_description: string;
      long_description: string;
    };
    business_tags: {
      tags: string[];
      main_business_category: string;
      main_industry: string;
      main_sector: string;
    };
    assets: {
      website: string;
      linkedin_url: string;
      facebook_url: string;
      instagram_url: string;
    };
    industry_snapshot: {
      total_competitors_in_location: number;
      total_competitors_in_continent: number;
    };
    geographic_dispersion: {
      top_10_cities: Array<{
        city: string;
        percentage: number;
      }>;
      country_breakdown: Array<{
        country: string;
        percentage: number;
      }>;
      continent_breakdown: Array<{
        continent: string;
        percentage: number;
      }>;
    };
    company_age: {
      average_year_founded: number;
      distribution_highlights: Record<string, number>;
    };
    trend_analysis: {
      short_description_themes: string[];
      business_tags: string[];
      product_technology_mentions: string[];
    };
  };
  status: string;
}

@Injectable({
  providedIn: 'root'
})
export class MarketingService {
  private apiUrl = environment.api.url;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHttpOptions(): Observable<{ headers: HttpHeaders }> {
    return this.authService.token().pipe(
      map(token => ({
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }),
      }))
    );
  }

  getFreeMarketingReport(email: string): Observable<MarketingReportResponse> {
    const url = `${this.apiUrl}/free-marketing-report`;
    const body: MarketingReportRequest = { email };

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<MarketingReportResponse>(url, body, options).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Failed to load marketing report: Invalid response structure');
            }
            return response;
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  registerLeadMagnet(request: LeadMagnetRequest): Observable<LeadMagnetResponse> {
    const url = `${this.apiUrl}/register/lead-magnet`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<LeadMagnetResponse>(url, request, options).pipe(
          catchError((error) => throwError(() => error))
        )
      )
    );
  }
}